import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { AdminDeletionConfirmationModal } from '../../components/AdminDeletionConfirmationModal';
import { apiService } from '../../services/api';
import { FileMetadata, FolderStructure } from '../../../../shared/types';
import { useAuth } from '../../contexts/AuthContext';
import { 
  HardDrive, 
  Trash2, 
  Search, 
  Filter, 
  RefreshCw, 
  CheckCircle,
  XCircle,
  Users,
  FolderOpen,
  File,
  Shield
} from 'lucide-react';
import { formatFileSize, formatDate } from '../../utils/formatters';

interface StorageManagementState {
  files: (FileMetadata & { ownerEmail: string })[];
  folders: (FolderStructure & { ownerEmail: string })[];
  loading: boolean;
  error: string | null;
  selectedFiles: (FileMetadata & { ownerEmail: string })[];
  selectedFolders: (FolderStructure & { ownerEmail: string })[];
  searchTerm: string;
  storageFilter: 'all' | 'upload' | 'archive';
  ownerFilter: string;
  showDeleteModal: boolean;
  isDeleting: boolean;
  successMessage: string | null;
}

export const StorageManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [state, setState] = useState<StorageManagementState>({
    files: [],
    folders: [],
    loading: true,
    error: null,
    selectedFiles: [],
    selectedFolders: [],
    searchTerm: '',
    storageFilter: 'all',
    ownerFilter: '',
    showDeleteModal: false,
    isDeleting: false,
    successMessage: null,
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const [filesResponse, foldersResponse] = await Promise.all([
        apiService.listAllFiles(),
        apiService.listAllFolders()
      ]);

      setState(prev => ({
        ...prev,
        files: filesResponse.files,
        folders: foldersResponse.folders,
        loading: false,
        selectedFiles: [],
        selectedFolders: [],
      }));
    } catch (error) {
      console.error('Failed to load storage data:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load storage data. Please try again.',
      }));
    }
  };

  // Get unique owners for filter dropdown
  const uniqueOwners = Array.from(new Set([
    ...state.files.map(f => f.ownerEmail),
    ...state.folders.map(f => f.ownerEmail)
  ])).sort();

  // Filter data based on search and filters
  const filteredFiles = state.files.filter(file => {
    const matchesSearch = file.fileName.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
                         file.ownerEmail.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
                         file.folderPath.toLowerCase().includes(state.searchTerm.toLowerCase());
    
    const matchesStorageFilter = state.storageFilter === 'all' || file.storageTier === state.storageFilter;
    const matchesOwnerFilter = !state.ownerFilter || file.ownerEmail === state.ownerFilter;
    
    return matchesSearch && matchesStorageFilter && matchesOwnerFilter;
  });

  const filteredFolders = state.folders.filter(folder => {
    const matchesSearch = folder.concertName.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
                         folder.ownerEmail.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
                         folder.folderPath.toLowerCase().includes(state.searchTerm.toLowerCase());
    
    const matchesStorageFilter = state.storageFilter === 'all' || 
                                (state.storageFilter === 'upload' && folder.folderPath.startsWith('/upload')) ||
                                (state.storageFilter === 'archive' && folder.folderPath.startsWith('/archive'));
    
    const matchesOwnerFilter = !state.ownerFilter || folder.ownerEmail === state.ownerFilter;
    
    return matchesSearch && matchesStorageFilter && matchesOwnerFilter;
  });

  // Selection handlers
  const handleFileSelection = (file: FileMetadata & { ownerEmail: string }, selected: boolean) => {
    setState(prev => ({
      ...prev,
      selectedFiles: selected
        ? [...prev.selectedFiles, file]
        : prev.selectedFiles.filter(f => f.fileId !== file.fileId)
    }));
  };

  const handleFolderSelection = (folder: FolderStructure & { ownerEmail: string }, selected: boolean) => {
    setState(prev => ({
      ...prev,
      selectedFolders: selected
        ? [...prev.selectedFolders, folder]
        : prev.selectedFolders.filter(f => f.folderPath !== folder.folderPath || f.userId !== folder.userId)
    }));
  };

  const handleSelectAll = (type: 'files' | 'folders') => {
    if (type === 'files') {
      const allSelected = filteredFiles.every(file => 
        state.selectedFiles.some(sf => sf.fileId === file.fileId)
      );
      
      setState(prev => ({
        ...prev,
        selectedFiles: allSelected ? [] : filteredFiles
      }));
    } else {
      const allSelected = filteredFolders.every(folder => 
        state.selectedFolders.some(sf => sf.folderPath === folder.folderPath && sf.userId === folder.userId)
      );
      
      setState(prev => ({
        ...prev,
        selectedFolders: allSelected ? [] : filteredFolders
      }));
    }
  };

  // Delete handlers
  const handleDeleteSelected = () => {
    if (state.selectedFiles.length === 0 && state.selectedFolders.length === 0) return;
    setState(prev => ({ ...prev, showDeleteModal: true }));
  };

  const handleConfirmDelete = async () => {
    try {
      setState(prev => ({ ...prev, isDeleting: true, error: null }));

      // Delete files first
      for (const file of state.selectedFiles) {
        await apiService.adminDeleteFile(file.fileId);
      }

      // Then delete folders
      for (const folder of state.selectedFolders) {
        // We need to construct a folder ID - using folderPath for now
        // This might need to be adjusted based on the actual API implementation
        const folderId = `${folder.userId}:${folder.folderPath}`;
        await apiService.adminDeleteFolder(folderId);
      }

      setState(prev => ({
        ...prev,
        isDeleting: false,
        showDeleteModal: false,
        selectedFiles: [],
        selectedFolders: [],
        successMessage: `Successfully deleted ${state.selectedFiles.length + state.selectedFolders.length} items as administrator.`,
      }));

      // Clear success message after 5 seconds
      setTimeout(() => {
        setState(prev => ({ ...prev, successMessage: null }));
      }, 5000);

      // Reload data
      await loadData();

    } catch (error) {
      console.error('Failed to delete items:', error);
      setState(prev => ({
        ...prev,
        isDeleting: false,
        error: 'Failed to delete some items. Please try again.',
      }));
    }
  };

  // Clear selections
  const clearSelections = () => {
    setState(prev => ({
      ...prev,
      selectedFiles: [],
      selectedFolders: [],
    }));
  };

  if (!currentUser?.isStorageAdmin) {
    return (
      <Layout>
        <div className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <XCircle className="h-6 w-6 text-red-500 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">Access Denied</h3>
                  <p className="text-red-700 mt-1">
                    You need Storage Administrator privileges to access storage management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <HardDrive className="h-8 w-8 mr-3 text-primary-600" />
                  Storage Management
                </h1>
                <p className="text-gray-600 mt-2">
                  Manage all users' files and folders across the system
                </p>
              </div>
              <button
                onClick={loadData}
                disabled={state.loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${state.loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Success Message */}
          {state.successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <p className="text-green-800">{state.successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {state.error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-red-800">{state.error}</p>
                <button
                  onClick={loadData}
                  className="ml-auto text-red-600 hover:text-red-800 underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Admin Warning */}
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex items-start">
              <Shield className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">
                  Storage Administrator Mode
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  You can delete any user's files and folders, including Archive Tier items regardless of eligibility. 
                  All deletion actions will be logged with your administrator identity.
                </p>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="mb-6 bg-white shadow rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search files, folders, or owners..."
                  value={state.searchTerm}
                  onChange={(e) => setState(prev => ({ ...prev, searchTerm: e.target.value }))}
                  className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Storage Tier Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={state.storageFilter}
                  onChange={(e) => setState(prev => ({ ...prev, storageFilter: e.target.value as any }))}
                  className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Storage Tiers</option>
                  <option value="upload">Upload Tier</option>
                  <option value="archive">Archive Tier</option>
                </select>
              </div>

              {/* Owner Filter */}
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={state.ownerFilter}
                  onChange={(e) => setState(prev => ({ ...prev, ownerFilter: e.target.value }))}
                  className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Owners</option>
                  {uniqueOwners.map(owner => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => setState(prev => ({ 
                  ...prev, 
                  searchTerm: '', 
                  storageFilter: 'all', 
                  ownerFilter: '',
                  selectedFiles: [],
                  selectedFolders: []
                }))}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Selection Actions */}
          {(state.selectedFiles.length > 0 || state.selectedFolders.length > 0) && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-900">
                    {state.selectedFiles.length + state.selectedFolders.length} items selected
                  </span>
                  <button
                    onClick={clearSelections}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear selection
                  </button>
                </div>
                <button
                  onClick={handleDeleteSelected}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Admin Delete Selected
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {state.loading ? (
            <div className="bg-white shadow rounded-lg p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="ml-3 text-gray-600">Loading storage data...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-center">
                    <File className="h-8 w-8 text-blue-500" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Files</p>
                      <p className="text-2xl font-bold text-gray-900">{state.files.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-center">
                    <FolderOpen className="h-8 w-8 text-green-500" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Folders</p>
                      <p className="text-2xl font-bold text-gray-900">{state.folders.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-purple-500" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Unique Owners</p>
                      <p className="text-2xl font-bold text-gray-900">{uniqueOwners.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-center">
                    <HardDrive className="h-8 w-8 text-orange-500" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Storage</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatFileSize(
                          state.files.reduce((sum, file) => sum + file.sizeBytes, 0) +
                          state.folders.reduce((sum, folder) => sum + folder.totalSizeBytes, 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Files Table */}
              <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Files ({filteredFiles.length})
                    </h2>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filteredFiles.length > 0 && filteredFiles.every(file => 
                          state.selectedFiles.some(sf => sf.fileId === file.fileId)
                        )}
                        onChange={() => handleSelectAll('files')}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="text-sm text-gray-600">Select all files</label>
                    </div>
                  </div>
                </div>

                {filteredFiles.length === 0 ? (
                  <div className="p-8 text-center">
                    <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No files found matching your criteria</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Select
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            File
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Owner
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Storage Tier
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Upload Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredFiles.map((file) => (
                          <tr key={file.fileId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={state.selectedFiles.some(sf => sf.fileId === file.fileId)}
                                onChange={(e) => handleFileSelection(file, e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <File className="h-5 w-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                    {file.fileName}
                                  </div>
                                  <div className="text-sm text-gray-500 truncate max-w-xs">
                                    {file.folderPath}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{file.ownerEmail}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatFileSize(file.sizeBytes)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                file.storageTier === 'upload'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {file.storageTier === 'upload' ? 'Upload' : 'Archive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatDate(file.uploadDate)}</div>
                              {file.archivalDate && (
                                <div className="text-xs text-gray-500">
                                  Archived: {formatDate(file.archivalDate)}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setState(prev => ({ 
                                    ...prev, 
                                    selectedFiles: [file],
                                    selectedFolders: [],
                                    showDeleteModal: true 
                                  }));
                                }}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Folders Table */}
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Folders ({filteredFolders.length})
                    </h2>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filteredFolders.length > 0 && filteredFolders.every(folder => 
                          state.selectedFolders.some(sf => sf.folderPath === folder.folderPath && sf.userId === folder.userId)
                        )}
                        onChange={() => handleSelectAll('folders')}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="text-sm text-gray-600">Select all folders</label>
                    </div>
                  </div>
                </div>

                {filteredFolders.length === 0 ? (
                  <div className="p-8 text-center">
                    <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No folders found matching your criteria</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Select
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Folder
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Owner
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Files
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Storage Tier
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredFolders.map((folder, index) => (
                          <tr key={`${folder.userId}-${folder.folderPath}-${index}`} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={state.selectedFolders.some(sf => sf.folderPath === folder.folderPath && sf.userId === folder.userId)}
                                onChange={(e) => handleFolderSelection(folder, e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <FolderOpen className="h-5 w-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {folder.concertName}
                                  </div>
                                  <div className="text-sm text-gray-500 truncate max-w-xs">
                                    {folder.folderPath}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{folder.ownerEmail}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{folder.fileCount}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatFileSize(folder.totalSizeBytes)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                folder.folderPath.startsWith('/upload')
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {folder.folderPath.startsWith('/upload') ? 'Upload' : 'Archive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatDate(folder.createdDate)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setState(prev => ({ 
                                    ...prev, 
                                    selectedFiles: [],
                                    selectedFolders: [folder],
                                    showDeleteModal: true 
                                  }));
                                }}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Admin Deletion Confirmation Modal */}
          <AdminDeletionConfirmationModal
            isOpen={state.showDeleteModal}
            onClose={() => setState(prev => ({ ...prev, showDeleteModal: false }))}
            onConfirm={handleConfirmDelete}
            selectedFiles={state.selectedFiles}
            selectedFolders={state.selectedFolders}
            isDeleting={state.isDeleting}
            adminEmail={currentUser?.email || ''}
          />
        </div>
      </div>
    </Layout>
  );
};